import { ArrowDownUp, LoaderCircle } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { formatEther, type Address } from "viem";

import { publicClient } from "@stonkland/protocol/chain";
import { amountLabel } from "@web/features/terminal/presentation";
import {
  LAND_POOL_FEE,
  LAND_SWAP_GAS_RESERVE,
  LAND_SWAP_SLIPPAGE_OPTIONS,
  parseEthAmount,
  parseLandAmount,
  quoteEthToLand,
  quoteLandToEth,
  type LandSwapQuote,
} from "@web/lib/land-swap";
import { readableError } from "@web/toasts/error-message";

type BuyLandPanelProps = {
  account?: Address;
  busy?: string;
  landBalance?: bigint;
  protocolLive: boolean;
  onBuyLand: (options: {
    amountIn: bigint;
    slippageBps: number;
  }) => Promise<boolean>;
  onSellLand: (options: {
    amountIn: bigint;
    slippageBps: number;
  }) => Promise<boolean>;
};

export function BuyLandPanel({
  account,
  busy,
  landBalance,
  protocolLive,
  onBuyLand,
  onSellLand,
}: BuyLandPanelProps) {
  const [direction, setDirection] = useState<"buy" | "sell">("buy");
  const [swapAmount, setSwapAmount] = useState("0.01");
  const [nativeBalance, setNativeBalance] = useState<bigint>();
  const [quote, setQuote] = useState<LandSwapQuote>();
  const [quoteError, setQuoteError] = useState<string>();
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [slippageBps, setSlippageBps] = useState(100);
  const amountIn = useMemo(
    () =>
      direction === "buy"
        ? parseEthAmount(swapAmount)
        : parseLandAmount(swapAmount),
    [direction, swapAmount],
  );

  useEffect(() => {
    if (!account) {
      let cancelled = false;
      queueMicrotask(() => {
        if (!cancelled) setNativeBalance(undefined);
      });
      return () => {
        cancelled = true;
      };
    }
    let cancelled = false;
    void publicClient
      .getBalance({ address: account })
      .then((balance) => {
        if (!cancelled) setNativeBalance(balance);
      })
      .catch(() => {
        if (!cancelled) setNativeBalance(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [account, refreshNonce]);

  useEffect(() => {
    if (!amountIn || amountIn <= 0n) {
      let cancelled = false;
      queueMicrotask(() => {
        if (!cancelled) {
          setQuote(undefined);
          setQuoteError(undefined);
          setQuoteLoading(false);
        }
      });
      return () => {
        cancelled = true;
      };
    }

    let cancelled = false;
    const timer = window.setTimeout(() => {
      setQuoteLoading(true);
      setQuoteError(undefined);
      const loadQuote =
        direction === "buy" ? quoteEthToLand : quoteLandToEth;
      void loadQuote(amountIn, slippageBps)
        .then((nextQuote) => {
          if (!cancelled) setQuote(nextQuote);
        })
        .catch((error: unknown) => {
          if (cancelled) return;
          setQuote(undefined);
          setQuoteError(
            readableError(
              error,
              "Could not load a live Uniswap v4 LAND quote.",
            ),
          );
        })
        .finally(() => {
          if (!cancelled) setQuoteLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [amountIn, direction, refreshNonce, slippageBps]);

  const reserveShortfall =
    nativeBalance !== undefined &&
    (direction === "buy"
      ? amountIn !== undefined &&
        nativeBalance < amountIn + LAND_SWAP_GAS_RESERVE
      : nativeBalance < LAND_SWAP_GAS_RESERVE);
  const landShortfall =
    direction === "sell" &&
    amountIn !== undefined &&
    landBalance !== undefined &&
    landBalance < amountIn;
  const rate =
    quote && quote.amountIn > 0n
      ? (quote.amountOut * 10n ** 18n) / quote.amountIn
      : undefined;
  const canSubmit = Boolean(
    protocolLive &&
      account &&
      quote &&
      amountIn &&
      amountIn > 0n &&
      !reserveShortfall &&
      !landShortfall &&
      !quoteLoading &&
      !busy,
  );

  const useMaximum = () => {
    if (direction === "sell") {
      if (landBalance && landBalance > 0n) setSwapAmount(formatEther(landBalance));
      return;
    }
    if (nativeBalance && nativeBalance > LAND_SWAP_GAS_RESERVE) {
      setSwapAmount(formatEther(nativeBalance - LAND_SWAP_GAS_RESERVE));
    }
  };

  const switchDirection = () => {
    const nextDirection = direction === "buy" ? "sell" : "buy";
    setDirection(nextDirection);
    setSwapAmount(nextDirection === "buy" ? "0.01" : "");
    setQuote(undefined);
    setQuoteError(undefined);
  };

  const submit = async () => {
    if (!canSubmit || !amountIn) return;
    const confirmed = await (direction === "buy" ? onBuyLand : onSellLand)({
      amountIn,
      slippageBps,
    });
    if (confirmed) setRefreshNonce((current) => current + 1);
  };

  const inputAsset = direction === "buy" ? "ETH" : "LAND";
  const outputAsset = direction === "buy" ? "LAND" : "ETH";
  const inputBalance = direction === "buy" ? nativeBalance : landBalance;
  const outputBalance = direction === "buy" ? landBalance : nativeBalance;

  return (
    <aside className="wide-panel panel land-swap-panel" id="buy-land">
      <div className="land-swap-copy">
        <span className="section-kicker">LAND TRADING</span>
        <h3>{direction === "buy" ? "BUY LAND WITH ETH." : "SELL LAND FOR ETH."}</h3>
        <p>
          {direction === "buy"
            ? "Swap directly through the canonical Uniswap v4 LAND/ETH pool. Your wallet receives LAND immediately—no token approval is required."
            : "Convert LAND back to native ETH through the same canonical pool. The first sale authorizes Permit2 before Uniswap executes it."}
        </p>
        <div className="land-swap-badges" aria-label="LAND swap properties">
          <span>{LAND_POOL_FEE / 10_000}% POOL FEE</span>
          <span>LIVE V4 QUOTER</span>
          <span>20 MIN DEADLINE</span>
        </div>
      </div>

      <div className="land-swap-ticket">
        <label className="swap-amount-field">
          <span>YOU PAY</span>
          <div>
            <input
              aria-label={`${inputAsset} amount to swap`}
              autoComplete="off"
              inputMode="decimal"
              onChange={(event) => setSwapAmount(event.target.value)}
              placeholder="0.00"
              value={swapAmount}
            />
            <strong>{inputAsset}</strong>
          </div>
          <small>
            Balance: {amountLabel(inputBalance, 6)} {inputAsset}
            {inputBalance &&
            (direction === "sell" || inputBalance > LAND_SWAP_GAS_RESERVE) ? (
              <button type="button" onClick={useMaximum}>
                MAX
              </button>
            ) : null}
          </small>
        </label>

        <button
          aria-label={`Switch to ${direction === "buy" ? "selling LAND" : "buying LAND"}`}
          className="swap-direction"
          onClick={switchDirection}
          title={`Switch to ${direction === "buy" ? "sell" : "buy"}`}
          type="button"
        >
          <ArrowDownUp aria-hidden="true" size={15} />
        </button>

        <div className="swap-output-field" aria-live="polite">
          <span>ESTIMATED OUTPUT</span>
          <div>
            <strong>
              {quoteLoading
                  ? "QUOTING…"
                  : quote
                  ? amountLabel(quote.amountOut, direction === "buy" ? 2 : 6)
                  : "—"}
            </strong>
            <b>{outputAsset}</b>
          </div>
          <small>
            Current balance: {amountLabel(outputBalance, 6)} {outputAsset}
          </small>
        </div>

        <div className="slippage-control">
          <span>MAX SLIPPAGE</span>
          <div>
            {LAND_SWAP_SLIPPAGE_OPTIONS.map((option) => (
              <button
                className={slippageBps === option ? "selected" : ""}
                key={option}
                onClick={() => setSlippageBps(option)}
                type="button"
              >
                {option / 100}%
              </button>
            ))}
          </div>
        </div>

        <div className="swap-quote-details">
          <span>
            <small>MINIMUM RECEIVED</small>
            <b>
              {quote
                ? `${amountLabel(quote.minimumAmountOut, direction === "buy" ? 2 : 6)} ${outputAsset}`
                : "—"}
            </b>
          </span>
          <span>
            <small>LIVE RATE</small>
            <b>
              {rate
                ? `${amountLabel(rate, direction === "buy" ? 0 : 8)} ${outputAsset} / ${inputAsset}`
                : "—"}
            </b>
          </span>
        </div>

        {landShortfall ? (
          <p className="swap-inline-error">
            The entered amount exceeds your LAND balance.
          </p>
        ) : reserveShortfall ? (
          <p className="swap-inline-error">
            Keep at least 0.001 ETH after the swap for network gas.
          </p>
        ) : quoteError ? (
          <p className="swap-inline-error">{quoteError}</p>
        ) : null}

        <button
          className="ticket-button land-buy-button"
          disabled={!canSubmit}
          onClick={() => void submit()}
          type="button"
        >
          {busy === (direction === "buy" ? "buy-land" : "sell-land") ? (
            direction === "buy" ? "BUYING LAND…" : "SELLING LAND…"
          ) : quoteLoading ? (
            <>
              <LoaderCircle className="spin" size={14} /> QUOTING
            </>
          ) : !account ? (
            `CONNECT WALLET TO ${direction === "buy" ? "BUY" : "SELL"}`
          ) : (
            direction === "buy" ? "BUY LAND" : "SELL LAND"
          )}
        </button>

      </div>
    </aside>
  );
}
